import React from 'react';
import KoobInputSelect, {
  type KoobSelectOption,
  type KoobSelectValue,
} from './KoobInputSelect';
import KoobInputTree, { type KoobTreeItem } from './KoobInputTree';

/** Leaf value → right value. A missing key means no right. */
export type KoobRightsValue = Record<string, KoobSelectValue | undefined>;

type KoobInputRightsProps = {
  items: KoobTreeItem[];
  rights: KoobSelectOption[];
  value: KoobRightsValue;
  onValueChange: (value: KoobRightsValue) => void;
  /** Shown on a select with no right, and on a parent whose children differ */
  rightPlaceholder?: string;
  childrenLabel?: (count: number) => string;
  searchable?: boolean;
  placeholder?: string;
  emptyTitle?: string;
  emptySearchTitle?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  label?: React.ReactNode;
  required?: boolean;
  'data-testid'?: string;
  /** Not part of the API (see packages/ui/README.md) — stripped at runtime */
  className?: never;
};

/**
 * KoobInputTree where every row carries a right select. Picking a right on a
 * parent applies it to all its children; the parent shows the shared right
 * or the placeholder when children differ.
 */
const KoobInputRights = React.forwardRef<HTMLDivElement, KoobInputRightsProps>(
  (
    {
      items,
      rights,
      value,
      onValueChange,
      rightPlaceholder = 'No access',
      disabled,
      className,
      ...rest
    },
    ref,
  ) => {
    const leavesOf = (item: KoobTreeItem) =>
      item.children?.length ? item.children.map((c) => c.value) : [item.value];

    const rightOf = (item: KoobTreeItem) => {
      const [first, ...others] = leavesOf(item).map((v) => value?.[String(v)]);
      return others.every((r) => r === first) ? first : undefined;
    };

    const setRight = (item: KoobTreeItem, right: KoobSelectValue | null) => {
      const next = { ...value };
      leavesOf(item).forEach((v) => {
        if (right == null) delete next[String(v)];
        else next[String(v)] = right;
      });
      onValueChange(next);
    };

    return (
      <KoobInputTree
        ref={ref}
        items={items}
        disabled={disabled}
        renderTrailing={(item) => (
          <div className="w-40">
            <KoobInputSelect
              size="sm"
              options={rights}
              value={rightOf(item) ?? null}
              onValueChange={(v) => setRight(item, v)}
              placeholder={rightPlaceholder}
              clearable
              disabled={disabled}
              data-testid={`${rest['data-testid'] ?? 'rights'}-${item.value}-right`}
            />
          </div>
        )}
        {...rest}
      />
    );
  },
);

KoobInputRights.displayName = 'KoobInputRights';

export default KoobInputRights;
